import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/axios';

const API_URL = '/api/leads';

// ─── Thunks ───────────────────────────────────────────────

export const fetchCatalogLeads = createAsyncThunk(
  'catalogLeads/fetchCatalogLeads',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const { filters, page, limit } = getState().catalogLeads;
      const queryParams = {
        source: filters.source || 'catalog',
        ...(filters.status ? { status: filters.status } : {}),
        page,
        limit,
        ...params,
      };
      const response = await api.get(API_URL, { params: queryParams });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateLeadStatus = createAsyncThunk(
  'catalogLeads/updateLeadStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      await api.patch(`${API_URL}/${id}`, { status });
      return { id, status };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────

const initialState = {
  leads: [],
  total: 0,
  page: 1,
  limit: 20,
  filters: { source: 'catalog', status: '', search: '' },
  loading: false,
  error: null,
};

const catalogLeadsSlice = createSlice({
  name: 'catalogLeads',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 1;
    },
    resetFilters: (state) => {
      state.filters = { source: 'catalog', status: '', search: '' };
      state.page = 1;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCatalogLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCatalogLeads.fulfilled, (state, action) => {
        state.loading = false;
        const { leads = [], total = 0, page = 1, limit = 20 } = action.payload || {};
        // Append on subsequent pages, replace on page 1.
        state.leads = page > 1 ? [...state.leads, ...leads] : leads;
        state.total = total;
        state.page = page;
        state.limit = limit;
      })
      .addCase(fetchCatalogLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const lead = state.leads.find((l) => l.id === id);
        if (lead) lead.status = status;
      })
      .addCase(updateLeadStatus.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setFilter, resetFilters, setPage } = catalogLeadsSlice.actions;

export const selectCatalogLeads = (state) => state.catalogLeads;

export default catalogLeadsSlice.reducer;
